/**
 * 🤖 MOTOR DE AUTOMAÇÕES
 * Executa automações baseadas em mudanças de campos
 */

import { apiUrl } from './apiBase';

interface AutomationEngine {
  executeAutomations: (
    automacoes: any[],
    etapa: any,
    fieldValues: Record<string, any>,
    changedFieldId: string,
    onFieldVisibilityChange: (fieldId: string, visible: boolean, opcoesEspecificas?: string[]) => void,
    onMoveCard?: (etapaDestinoId: string, meta?: { automacaoId?: string; automacaoNome?: string; campoGatilho?: string }) => void,
    cardId?: string,
    onApiResult?: (resultado: { sucesso: boolean; mensagem: string; url: string }) => void
  ) => void;
}

// Evita múltiplos envios ao alternar valores: armazena o último resultado da condição por regra
const lastEmailRuleState: Map<string, boolean> = new Map();
// Armazena timestamp (ms) do último envio por regra (para WhatsApp)
const regraWhatsappUltimoEnvio: Map<string, number> = new Map();

// Utilitário: avalia condição com operadores adicionais e tipos diversos
function avaliarCondicao(valorCampo: any, operador: string, valorComparacao?: any): boolean {
  try {
    switch (operador) {
      case 'preenchido':
        return !!(valorCampo !== undefined && valorCampo !== null && String(valorCampo).trim() !== '');
      case 'vazio':
        return !(valorCampo !== undefined && valorCampo !== null && String(valorCampo).trim() !== '');
      case 'igual':
        return String(valorCampo) === String(valorComparacao ?? '');
      case 'diferente':
        return String(valorCampo) !== String(valorComparacao ?? '');
      case 'contem':
        return String(valorCampo ?? '').toLowerCase().includes(String(valorComparacao ?? '').toLowerCase());
      case 'nao_contem':
        return !String(valorCampo ?? '').toLowerCase().includes(String(valorComparacao ?? '').toLowerCase());
      case 'maior':
        return Number(valorCampo) > Number(valorComparacao);
      case 'menor':
        return Number(valorCampo) < Number(valorComparacao);
      case 'maior_igual':
        // Verifica se a data já passou (é igual ou anterior à data atual)
        if (!valorCampo) return false;
        try {
          const dataCampo = new Date(valorCampo);
          const hoje = new Date();
          
          // Remove as horas para comparar apenas as datas
          hoje.setHours(0, 0, 0, 0);
          dataCampo.setHours(0, 0, 0, 0);
          
          // Retorna true se a data do campo for igual ou anterior a hoje (já passou)
          const resultado = dataCampo <= hoje;
          console.log(`      📅 Data do campo: ${dataCampo.toLocaleDateString()}, Hoje: ${hoje.toLocaleDateString()}, Já passou: ${resultado}`);
          return resultado;
        } catch (error) {
          console.error('      ❌ Erro ao verificar data:', error);
          return false;
        }
      case 'dias_restantes': {
        // Calcula quantos dias faltam até a data do campo
        if (!valorCampo) return false;
        
        try {
          const dataAlvo = new Date(valorCampo);
          const hoje = new Date();
          
          // Remove as horas para comparar apenas as datas
          hoje.setHours(0, 0, 0, 0);
          dataAlvo.setHours(0, 0, 0, 0);
          
          // Calcula a diferença em dias
          const diferencaMs = dataAlvo.getTime() - hoje.getTime();
          const diasRestantes = Math.ceil(diferencaMs / (1000 * 60 * 60 * 24));
          
          // O valor de comparação é o número de dias configurado
          const diasConfigurados = Number(valorComparacao);
          
          console.log(`      📅 Dias restantes: ${diasRestantes}, Configurado: ${diasConfigurados}`);
          
          // Retorna true se os dias restantes forem MENOR OU IGUAL ao valor configurado
          // Exemplo: se faltam 30 dias e configurou 30, retorna true
          // se faltam 29 dias e configurou 30, retorna true
          // se faltam 31 dias e configurou 30, retorna false
          return diasRestantes <= diasConfigurados;
        } catch (error) {
          console.error('      ❌ Erro ao calcular dias restantes:', error);
          return false;
        }
      }
      default:
        console.log(`      ❌ Operador '${operador}' não suportado`);
        return false;
    }
  } catch {
    return false;
  }
}

// Utilitário: converte valores por id de campo para um dicionário por nome de campo (para templates)
function mapearValoresPorNome(etapa: any, fieldValues: Record<string, any>): Record<string, any> {
  const porNome: Record<string, any> = {};
  try {
    (etapa?.campos || []).forEach((c: any) => {
      if (Object.prototype.hasOwnProperty.call(fieldValues, c.id)) {
        porNome[c.nome] = fieldValues[c.id];
      }
    });
  } catch (e) {
    console.warn('Falha ao mapear valores por nome:', e);
  }
  return porNome;
}

export const automationEngine: AutomationEngine = {
  executeAutomations: (automacoes, etapa, fieldValues, changedFieldId, onFieldVisibilityChange, onMoveCard, cardId, onApiResult) => {
    console.log('🤖 Executando automações para campo:', changedFieldId);
    console.log('📋 Automações disponíveis:', automacoes.length);
    console.log('📄 Valores dos campos:', fieldValues);
    
    // Filtrar automações que se aplicam a esta etapa e estão ativas
    const automacoesAplicaveis = automacoes.filter(automacao => 
      (automacao?.status ?? 'ativo') === 'ativo' &&
      (automacao.etapaOrigemId === etapa.id || automacao.etapaOrigemId === 'all')
    );
    
    console.log('🎯 Automações aplicáveis:', automacoesAplicaveis.length);
    
    automacoesAplicaveis.forEach((automacao, index) => {
      console.log(`\n🔍 Processando automação ${index + 1}: ${automacao.nome}`);
      
  automacao.acoes.forEach((acao: any, acaoIndex: number) => {
        if (acao.tipo === 'exibir_ocultar') {
          console.log(`  ⚡ Processando ação ${acaoIndex + 1}: exibir_ocultar`);
          
          const config = acao.configuracao;
          const regras = config.regras || [];
          
      regras.forEach((regra: any, regraIndex: number) => {
            console.log(`    📏 Processando regra ${regraIndex + 1}`);
            
            const condicao = regra.condicao;
            const campoIds = condicao?.campoIds || (condicao?.campoId ? [condicao.campoId] : []);
            const campoCondicao = campoIds[0]; // backward compat / principal
            const operador = condicao?.operador;
            
            console.log(`      🔍 Condição: campos [${campoIds.join(', ')}] ${operador}`);
            
            // Só processar se a mudança afetou algum campo da condição
            if (!campoIds.includes(changedFieldId) && changedFieldId !== 'initial') {
              console.log(`      ⏭️ Campo ${changedFieldId} não afeta esta regra`);
              return;
            }
            
            // Avaliar condição principal - todos os campoIds devem atender (AND)
            let condicaoAtendida = true;
            for (const cId of campoIds) {
              const valorCampo = fieldValues[cId];
              console.log(`      📊 Valor do campo '${cId}': '${valorCampo}'`);
              if (!avaliarCondicao(valorCampo, operador, condicao?.valor)) {
                condicaoAtendida = false;
                break;
              }
            }
            
            // Avaliar condições E (AND) e OU (OR) adicionais
            if (regra.condicoesAdicionais && regra.condicoesAdicionais.length > 0) {
              // Separar condições AND e OR
              const condicoesAND = regra.condicoesAdicionais.filter((c: any) => c.tipoLogico !== 'OU');
              const condicoesOU = regra.condicoesAdicionais.filter((c: any) => c.tipoLogico === 'OU');
              
              // Todas as condições AND devem ser verdadeiras
              if (condicaoAtendida) {
                for (const condE of condicoesAND) {
                  if (condE.campoId) {
                    const valorCampoE = fieldValues[condE.campoId];
                    const condEAtendida = avaliarCondicao(valorCampoE, condE.operador, condE.valor);
                    console.log(`      🔗 Condição E: campo '${condE.campoId}' ${condE.operador} = ${condEAtendida}`);
                    if (!condEAtendida) {
                      condicaoAtendida = false;
                      break;
                    }
                  }
                }
              }
              
              // Se há condições OU: a condição principal (com ANDs) OU qualquer condição OU deve ser verdadeira
              if (condicoesOU.length > 0) {
                let algumOUAtendido = false;
                for (const condOU of condicoesOU) {
                  if (condOU.campoId) {
                    const valorCampoOU = fieldValues[condOU.campoId];
                    const condOUAtendida = avaliarCondicao(valorCampoOU, condOU.operador, condOU.valor);
                    console.log(`      🔀 Condição OU: campo '${condOU.campoId}' ${condOU.operador} = ${condOUAtendida}`);
                    if (condOUAtendida) {
                      algumOUAtendido = true;
                      break;
                    }
                  }
                }
                condicaoAtendida = condicaoAtendida || algumOUAtendido;
              }
            }
            
            console.log(`      ✅ Condição final atendida: ${condicaoAtendida}`);
            
            // Compatibilidade: suportar dois formatos de regra
            // Formato A (antigo): { acao: 'mostrar'|'esconder', elementosAfetados: [{id}] }
            // Formato B (novo): { acaoEntao: {acao, campo}, acaoSenao?: {acao, campo}, elementosAfetadosSenao?: [...] }

            const aplicarAcao = (alvos: string[], tipoAcao: string, contexto: 'ENTAO'|'SENAO', elementos?: any[]) => {
              const mostrar = tipoAcao === 'mostrar';
              alvos.forEach((campoAlvo) => {
                if (!campoAlvo) return;
                const elemento = elementos?.find((e: any) => e?.id === campoAlvo);
                const opcoesEspecificas = elemento?.opcoesEspecificas;
                
                if (opcoesEspecificas && opcoesEspecificas.length > 0) {
                  console.log(`      🎯 [${contexto}] Campo alvo: '${campoAlvo}', Ação: '${tipoAcao}', Opções específicas: [${opcoesEspecificas.join(', ')}]`);
                  // Passa as opções específicas para o callback
                  onFieldVisibilityChange(campoAlvo, mostrar, opcoesEspecificas);
                  console.log(`      ✨ Campo '${campoAlvo}' - Opções [${opcoesEspecificas.join(', ')}] ${mostrar ? 'MOSTRADAS' : 'OCULTAS'}`);
                } else {
                  console.log(`      🎯 [${contexto}] Campo alvo: '${campoAlvo}', Ação: '${tipoAcao}'`);
                  onFieldVisibilityChange(campoAlvo, mostrar);
                  console.log(`      ✨ Campo '${campoAlvo}' ${mostrar ? 'MOSTRADO' : 'OCULTO'}`);
                }
              });
            };

            // Resolver alvos e ações conforme o formato
            const resolveThen = () => {
              // Novo formato prioritário
              if (regra.acaoEntao && regra.acaoEntao.campo) {
                return { alvos: [regra.acaoEntao.campo], tipo: regra.acaoEntao.acao || 'mostrar', elementos: [] };
              }
              // Antigo formato
              const elementos = regra.elementosAfetados || [];
              const alvosAntigos = elementos
                .map((e: any) => e?.id)
                .filter(Boolean);
              if (alvosAntigos.length > 0) {
                return { alvos: alvosAntigos, tipo: regra.acao || regra.comportamento || 'mostrar', elementos };
              }
              return null;
            };

            const resolveElse = () => {
              // Novo formato prioritário
              if (regra.acaoSenao && regra.acaoSenao.campo) {
                return { alvos: [regra.acaoSenao.campo], tipo: regra.acaoSenao.acao || 'esconder', elementos: [] };
              }
              // Antigo formato com elementosAfetadosSenao
              const elementosSenao = regra.elementosAfetadosSenao || [];
              const alvosSenaoAntigos = elementosSenao
                .map((e: any) => e?.id)
                .filter(Boolean);
              if (alvosSenaoAntigos.length > 0) {
                // Se não houver tipo específico, inverte a ação principal
                const principal = resolveThen();
                const tipo = (regra.acaoSenao || (principal?.tipo === 'mostrar' ? 'esconder' : 'mostrar'));
                return { alvos: alvosSenaoAntigos, tipo, elementos: elementosSenao };
              }
              // Se não houver definição de SENÃO, inverte a ação principal nos mesmos alvos
              const principal = resolveThen();
              if (principal) {
                const tipoInvertido = principal.tipo === 'mostrar' ? 'esconder' : 'mostrar';
                return { alvos: principal.alvos, tipo: tipoInvertido, elementos: principal.elementos };
              }
              return null;
            };

            if (condicaoAtendida) {
              const entao = resolveThen();
              if (entao) aplicarAcao(entao.alvos, entao.tipo, 'ENTAO', entao.elementos);
              // Ações ENTÃO adicionais
              if (regra.acoesEntaoAdicionais && regra.acoesEntaoAdicionais.length > 0) {
                regra.acoesEntaoAdicionais.forEach((extra: any) => {
                  if (extra.elemento?.id) {
                    aplicarAcao([extra.elemento.id], extra.acao || 'esconder', 'ENTAO', [extra.elemento]);
                  }
                });
              }
            } else {
              const senao = resolveElse();
              if (senao) aplicarAcao(senao.alvos, senao.tipo, 'SENAO', senao.elementos);
              // Ações SENÃO adicionais
              if (regra.acoesSenaoAdicionais && regra.acoesSenaoAdicionais.length > 0) {
                regra.acoesSenaoAdicionais.forEach((extra: any) => {
                  if (extra.elemento?.id) {
                    aplicarAcao([extra.elemento.id], extra.acao || 'mostrar', 'SENAO', [extra.elemento]);
                  }
                });
              }
            }
          });
        }

        // Envio condicional de e-mail (SE/ENTÃO/SENÃO)
        if (acao.tipo === 'enviar_email') {
          console.log(`  ⚡ Processando ação ${acaoIndex + 1}: enviar_email`);
          console.log(`  📋 changedFieldId: ${changedFieldId}`);
          console.log(`  🎯 fieldValues:`, fieldValues);
          const config = acao.configuracao || {};
          const regrasEmail = config.regrasEmail || [];

          console.log(`  📧 regrasEmail encontradas: ${regrasEmail.length}`);

          // Se não há regras, nada a fazer aqui (envio pode ser manual em outro ponto)
          if (!Array.isArray(regrasEmail) || regrasEmail.length === 0) {
            console.log(`  ⚠️ Nenhuma regra de e-mail configurada`);
            return;
          }

          regrasEmail.forEach((regra: any, regraIndex: number) => {
            const cond = regra?.condicao || {};
            const campoCond = cond.campoId;
            const op = cond.operador;
            const valorCmp = cond.valor;

            console.log(`  📋 Regra ${regraIndex + 1} - Campo: ${campoCond}, Op: ${op}, Valor: ${valorCmp}`);
            console.log(`  🔍 Valor do campo "${campoCond}": "${fieldValues[campoCond]}"`);
            console.log(`  📋 Todos os campos disponíveis:`, Object.keys(fieldValues));
            console.log('    🔧 Objeto regra bruto:', JSON.parse(JSON.stringify(regra)));

            // Nova lógica corrigida:
            // - No 'initial': sempre avaliamos todas as regras (estado inicial completo)
            // - Em mudanças: avaliamos se o campo alterado é o de condição OU
            //   se a condição é 'preenchido' e o campo da condição já está preenchido
            let deveProcessar = false;
            if (changedFieldId === 'initial') {
              deveProcessar = true;
            } else if (campoCond) {
              if (campoCond === changedFieldId) {
                deveProcessar = true;
              } else if (op === 'preenchido' && fieldValues[campoCond]) {
                deveProcessar = true; // permitir avaliação mesmo que outro campo tenha mudado
              }
            }

            if (!deveProcessar) {
              console.log(`  ⏭️ Pulando regra ${regraIndex + 1} - Campo não afetado ou não deve processar`);
              return;
            }

            const valorCampo = fieldValues[campoCond];
            const ok = avaliarCondicao(valorCampo, op, valorCmp);
            console.log(`    📧 Regra ${regraIndex + 1} - condição ${ok ? 'ATENDIDA' : 'NÃO ATENDIDA'}`);

            const ruleKeyBase = `${automacao.id || automacao.nome || 'auto'}:${cardId || 'noCard'}`;
            const ruleKey = `${ruleKeyBase}:email:${acaoIndex}:${regraIndex}`;
            const prev = lastEmailRuleState.get(ruleKey);

            // Determinar ação a executar conforme o estado atual da condição
            let enviar = false;
            let templateId: any = undefined;

            if (ok) {
              if (regra.acaoEntao?.tipo === 'enviar') {
                enviar = true;
                templateId = regra.acaoEntao?.templateId ?? config.templateId;
              }
            } else {
              if (regra.acaoSenao?.tipo === 'enviar') {
                enviar = true;
                templateId = regra.acaoSenao?.templateId ?? config.templateId;
              }
            }

            // Envia apenas quando há transição de estado (evita disparos repetidos)
            if (enviar) {
              const shouldFire = prev === undefined ? true : prev !== ok;
              if (shouldFire) {
                const valoresPorNome = mapearValoresPorNome(etapa, fieldValues);
                if (!templateId) {
                  console.warn('    ⚠️ Template ID ausente para envio de e-mail, ignorando.');
                } else {
                  console.log(`    ✉️ Disparando envio de e-mail do template ${templateId}`);
                  const payload = {
                    template_id: templateId,
                    field_values: valoresPorNome
                  };
                  console.log('    📦 Payload envio e-mail:', payload);
                  
                  try {
                    // Chamada ao backend para envio do e-mail via template
                    fetch(apiUrl('/enviar-email-template'), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(payload)
                    }).then(async (resp) => {
                      const texto = await resp.text();
                      let json: any = null;
                      try { json = JSON.parse(texto); } catch { /* manter texto cru */ }
                      
                      console.log('    📬 Resposta envio e-mail:', resp.status, json || texto);
                      
                      if (!resp.ok || (json && json.success === false)) {
                        console.warn('    ⚠️ Falha no envio e-mail (HTTP ou lógica):', json?.error || texto);
                      } else {
                        console.log('    ✅ Solicitação de envio de e-mail enviada com sucesso');
                      }
                    }).catch((err) => {
                      console.error('    ❌ Falha ao solicitar envio de e-mail:', err);
                    });
                  } catch (e) {
                    console.error('    ❌ Erro ao enviar e-mail:', e);
                  }
                }
              }
            }

            // Atualiza o último estado avaliado
            lastEmailRuleState.set(ruleKey, ok);
          });
        }

        // Envio condicional de WhatsApp (SE/ENTÃO/SENÃO)
        if (acao.tipo === 'enviar_whatsapp') {
          console.log(`  ⚡ Processando ação ${acaoIndex + 1}: enviar_whatsapp`);
          console.log(`  📋 changedFieldId: ${changedFieldId}`);
          console.log(`  🎯 fieldValues:`, fieldValues);
          const config = acao.configuracao || {};
          const regrasWhatsapp = config.regrasWhatsapp || [];

          console.log(`  📱 regrasWhatsapp encontradas: ${regrasWhatsapp.length}`);

          // Se não há regras, nada a fazer aqui (envio pode ser manual em outro ponto)
          if (!Array.isArray(regrasWhatsapp) || regrasWhatsapp.length === 0) {
            console.log(`  ⚠️ Nenhuma regra de WhatsApp configurada`);
            return;
          }

          regrasWhatsapp.forEach((regra: any, regraIndex: number) => {
            const cond = regra?.condicao || {};
            const campoCond = cond.campoId;
            const op = cond.operador;
            const valorCmp = cond.valor;

            console.log(`  📋 Regra ${regraIndex + 1} - Campo: ${campoCond}, Op: ${op}, Valor: ${valorCmp}`);
            console.log(`  🔍 Valor do campo "${campoCond}": "${fieldValues[campoCond]}"`);
            console.log(`  📋 Todos os campos disponíveis:`, Object.keys(fieldValues));
            console.log('    🔧 Objeto regra bruto:', JSON.parse(JSON.stringify(regra)));

            // Nova lógica:
            // - No 'initial': sempre avaliamos todas as regras (estado inicial completo)
            // - Em mudanças: avaliamos se o campo alterado é o de condição OU
            //   se a condição é 'preenchido' e o campo da condição já está preenchido (permite disparar baseado em outro campo dependente)
            //   (a proteção contra disparos repetidos continua via lastEmailRuleState/shouldFire)
            let deveProcessar = false;
            if (changedFieldId === 'initial') {
              deveProcessar = true;
            } else if (campoCond) {
              if (campoCond === changedFieldId) {
                deveProcessar = true;
              } else if (op === 'preenchido' && fieldValues[campoCond]) {
                deveProcessar = true; // permitir avaliação mesmo que outro campo tenha mudado
              }
            }

            if (!deveProcessar) {
              console.log(`  ⏭️ Pulando regra ${regraIndex + 1} - Campo não afetado ou não deve processar`);
              return;
            }

            const valorCampo = fieldValues[campoCond];
            const ok = avaliarCondicao(valorCampo, op, valorCmp);
            console.log(`    📱 Regra ${regraIndex + 1} - condição ${ok ? 'ATENDIDA' : 'NÃO ATENDIDA'} (valorCampo: "${valorCampo}")`);

            const ruleKeyBase = `${automacao.id || automacao.nome || 'auto'}:${cardId || 'noCard'}`;
            const ruleKey = `${ruleKeyBase}:whatsapp:${acaoIndex}:${regraIndex}`;
            const prev = lastEmailRuleState.get(ruleKey);
            console.log(`    🔁 ruleKey=${ruleKey} prev=${prev} novoEstadoOk=${ok}`);

            // Determinar ação a executar conforme o estado atual da condição
            let enviar = false;
            let templateId: any = undefined;
            let destinatariosOverride: string[] | undefined = undefined;
            let origemAcao: 'ENTAO' | 'SENAO' | undefined = undefined;

            if (ok) {
              if (regra.acaoEntao?.tipo === 'enviar') {
                enviar = true;
                templateId = regra.acaoEntao?.templateId ?? config.templateId;
                destinatariosOverride = regra.acaoEntao?.destinatarios?.length ? regra.acaoEntao.destinatarios : config.destinatarios;
                origemAcao = 'ENTAO';
              }
            } else {
              if (regra.acaoSenao?.tipo === 'enviar') {
                enviar = true;
                templateId = regra.acaoSenao?.templateId ?? config.templateId;
                destinatariosOverride = regra.acaoSenao?.destinatarios?.length ? regra.acaoSenao.destinatarios : config.destinatarios;
                origemAcao = 'SENAO';
              }
            }

            // Para WhatsApp: se enviarSomentePrimeiraVez=true, só dispara na transição false->true; caso contrário envia sempre que ok exigir
            if (enviar) {
              // Fallback: se templateId ausente, tentar derivar de outras fontes conhecidas
              if (!templateId) {
                const candidatos: any[] = [];
                if (regra.acaoEntao?.templateId) candidatos.push(regra.acaoEntao.templateId);
                if (regra.acaoSenao?.templateId) candidatos.push(regra.acaoSenao.templateId);
                if (config.templateId) candidatos.push(config.templateId);
                // Possíveis coleções de templates em config
                const colecoes = [config.templatesWhatsapp, config.whatsappTemplates, config.templates];
                colecoes.forEach(col => {
                  if (Array.isArray(col)) {
                    const ativo = col.find((t: any) => t && (t.ativo === true || t.status === 'ativo'));
                    if (ativo?.id) candidatos.push(ativo.id);
                    if (col[0]?.id) candidatos.push(col[0].id);
                  }
                });
                const escolhido = candidatos.find(Boolean);
                if (escolhido) {
                  templateId = escolhido;
                  console.log(`    🔄 Fallback templateId aplicado: ${templateId}`);
                } else {
                  console.warn('    ⚠️ Nenhum templateId encontrado (regra/config). Campos config:', Object.keys(config || {}));
                }
              }
              // Nova política anti-spam:
              // - Mesmo sem 'enviarSomentePrimeiraVez', só dispara quando há transição para estado ATENDIDO
              // - Com 'enviarSomentePrimeiraVez', idem (false->true) e nunca mais depois
              // Isso evita disparar a cada tecla quando a condição é 'preenchido'.
              // Normalização / migração implícita: se flag não definida, tratamos como true (enviar só primeira vez)
              const onlyFirst = config.enviarSomentePrimeiraVez === undefined ? true : !!config.enviarSomentePrimeiraVez;

              // Cooldown configurável quando onlyFirst = false
              const cooldownSegundos = typeof config.cooldownSegundos === 'number' && config.cooldownSegundos > 0 ? config.cooldownSegundos : 300; // default 5 min

              // Identificador do "card" não está disponível diretamente neste escopo; construir um hash simples baseado na automação
              const regraKey = `${ruleKeyBase}:whatsapp:${acaoIndex}:${regraIndex}`;
              const agora = Date.now();
              const ultimoEnvio = regraWhatsappUltimoEnvio.get(regraKey);

              let shouldFire = false;
              let motivoBloqueio: string | null = null;

              if (ok) {
                if (prev !== true) {
                  // Transição false->true sempre permite primeiro envio
                  shouldFire = true;
                } else if (!onlyFirst) {
                  // Condição permanece true e a regra permite repetições com cooldown
                  if (!ultimoEnvio) {
                    shouldFire = true;
                  } else {
                    const decorrido = (agora - ultimoEnvio) / 1000;
                    if (decorrido >= cooldownSegundos) {
                      shouldFire = true;
                    } else {
                      motivoBloqueio = `cooldown (${decorrido.toFixed(1)}s < ${cooldownSegundos}s)`;
                    }
                  }
                } else {
                  motivoBloqueio = 'onlyFirst=true (já enviado)';
                }
              } else {
                // Se condição ficou false novamente, permite novo ciclo de transição (prev true -> false -> future true)
                motivoBloqueio = 'condicao_false (rearmado)';
              }

              console.log(`    🧪 enviar=${shouldFire} origem=${origemAcao} templateId=${templateId} onlyFirst=${onlyFirst} prev=${prev} ok=${ok} ultimoEnvio=${ultimoEnvio ? (Math.round((agora-ultimoEnvio)/1000)+'s_ago') : 'nunca'} cooldown=${cooldownSegundos}s bloqueio=${motivoBloqueio}`);
              if (shouldFire) {
                const valoresPorNome = mapearValoresPorNome(etapa, fieldValues);
                if (!templateId) {
                  console.warn('    ⚠️ Template ID ausente para envio de WhatsApp, ignorando. config.templateId=', config.templateId);
                } else {
                  console.log(`    📱 Disparando envio de WhatsApp do template ${templateId}`);
                  try {
                    // Chamada ao backend para envio do WhatsApp via template
                    const payload: any = { template_id: templateId, field_values: valoresPorNome };
                    if (destinatariosOverride && destinatariosOverride.length) {
                      payload.destinatarios = destinatariosOverride;
                    }
                    console.log('    📦 Payload envio WhatsApp:', payload);
                    // Usar número configurado dinamicamente (sem fallback fixo)
                    const numeroConfig = config.numeroFixo || config.numeroDestino || config.numero;
                    if (!payload.destinatarios || payload.destinatarios.length === 0) {
                      if (numeroConfig) {
                        payload.numero = numeroConfig; // backend aceita 'numero' como override único
                      } else {
                        console.warn('⚠️ Nenhum destinatário ou número configurado para WhatsApp');
                      }
                    }
                    fetch(apiUrl('/enviar-whatsapp-template'), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(payload)
                    }).then(async (resp) => {
                      const texto = await resp.text();
                      let json: any = null;
                      try { json = JSON.parse(texto); } catch { /* manter texto cru */ }
                      if (json && json.falhas && Array.isArray(json.falhas) && json.falhas.length) {
                        console.warn('    📬 Resposta envio WhatsApp (falhas detalhadas):', json.falhas);
                      }
                      console.log('    📬 Resposta envio WhatsApp:', resp.status, json || texto);
                      if (!resp.ok || (json && json.success === false)) {
                        console.warn('    ⚠️ Falha no envio WhatsApp (HTTP ou lógica)');
                      } else {
                        console.log('    ✅ Solicitação de envio de WhatsApp enviada');
                      }
                    }).catch((err) => {
                      console.error('    ❌ Falha ao solicitar envio de WhatsApp:', err);
                    });
                  } catch (e) {
                    console.error('    ❌ Erro ao enviar WhatsApp:', e);
                  }
                }
              }
            } else {
              console.log(`    🚫 enviar=false (acaoEntao.tipo='${regra.acaoEntao?.tipo}' acaoSenao.tipo='${regra.acaoSenao?.tipo}' ok=${ok})`);
            }

            // Atualiza o último estado avaliado
            lastEmailRuleState.set(ruleKey, ok);
          });
        }

        // Mover card condicional (SE/ENTÃO/SENÃO)
        if (acao.tipo === 'mover_card') {
          console.log(`  ⚡ Processando ação ${acaoIndex + 1}: mover_card`);
          const config = acao.configuracao || {};
          const regrasMover = config.regrasMover || [];

          // Se não há regras, não fazemos nada aqui (um move global pode ser tratado no submit/mudança de etapa)
          if (!Array.isArray(regrasMover) || regrasMover.length === 0) return;

          regrasMover.forEach((regra: any, regraIndex: number) => {
            // Suporte a múltiplas condições (AND) via campo condicoes[], com fallback para condicao single
            const condicoes: any[] = regra.condicoes?.length > 0 ? regra.condicoes : (regra.condicao ? [regra.condicao] : []);
            if (condicoes.length === 0) return;

            // Processar em 'initial' ou quando algum dos campos de condição mudar
            if (changedFieldId !== 'initial') {
              const algumCampoAfetado = condicoes.some((c: any) => !c.campoId || c.campoId === changedFieldId);
              if (!algumCampoAfetado) return;
            }

            // Avaliar todas as condições com AND
            const ok = condicoes.every((cond: any) => {
              const valorCampo = fieldValues[cond.campoId];
              return avaliarCondicao(valorCampo, cond.operador, cond.valor);
            });
            console.log(`    🔄 Regra mover ${regraIndex + 1} - condição${condicoes.length > 1 ? 'ões (E)' : ''} ${ok ? 'ATENDIDA' : 'NÃO ATENDIDA'}`);

            let mover = false;
            let destino: string | undefined = undefined;

            if (ok) {
              if (regra.acaoEntao?.tipo === 'mover') {
                mover = true;
                destino = regra.acaoEntao?.etapaDestinoId || config.etapaDestinoId;
              }
            } else if (regra.acaoSenao?.tipo === 'mover') {
              mover = true;
              destino = regra.acaoSenao?.etapaDestinoId || config.etapaDestinoId;
            }

      if (mover && destino) {
              console.log(`    🚚 Solicitar mover card para etapa '${destino}'`);
              if (onMoveCard) {
                try { 
                  // Incluir o campoGatilho: campo que mudou (ou primeiro campo das condições)
                  const campoGatilho = changedFieldId !== 'initial' ? changedFieldId : (condicoes[0]?.campoId);
                  onMoveCard(destino, { 
                    automacaoId: automacao.id, 
                    automacaoNome: automacao.nome,
                    campoGatilho // 🔧 Campo que trigou a automação - deve ser limpo!
                  }); 
                } catch (e) { 
                  console.warn('onMoveCard falhou:', e); 
                }
              }
            }
          });
        }

        // Atribuir responsável condicional (SE/ENTÃO/SENÃO)
        if (acao.tipo === 'atribuir_responsavel') {
          console.log(`  ⚡ Processando ação ${acaoIndex + 1}: atribuir_responsavel`);
          const config = acao.configuracao || {};
          const regrasResponsavel = config.regrasResponsavel || [];

          if (!Array.isArray(regrasResponsavel) || regrasResponsavel.length === 0) {
            console.log(`  ⚠️ Nenhuma regra de responsável configurada`);
            return;
          }

          regrasResponsavel.forEach((regra: any, regraIndex: number) => {
            const cond = regra?.condicao || {};
            const campoCond = cond.campoId;
            const op = cond.operador;
            const valorCmp = cond.valor;

            console.log(`  📋 Regra ${regraIndex + 1} - Campo: ${campoCond}, Op: ${op}, Valor: ${valorCmp}`);
            console.log(`  🔍 Valor do campo "${campoCond}": "${fieldValues[campoCond]}"`);

            // Só processar se a mudança afetou o campo da condição
            // NÃO executar automaticamente em 'initial' - precisa atender condição
            if (campoCond !== changedFieldId) {
              console.log(`  ⏭️ Pulando regra ${regraIndex + 1} - Campo não afetado`);
              return;
            }

            const valorCampo = fieldValues[campoCond];
            const ok = avaliarCondicao(valorCampo, op, valorCmp);
            console.log(`    👤 Regra ${regraIndex + 1} - condição ${ok ? 'ATENDIDA' : 'NÃO ATENDIDA'}`);

            let responsavelId: string | null = null;

            if (ok && regra.responsavelId) {
              responsavelId = regra.responsavelId;
              console.log(`    ✅ ENTÃO atribuir ao responsável: ${responsavelId}`);
            } else if (!ok && regra.senao?.responsavelId) {
              responsavelId = regra.senao.responsavelId;
              console.log(`    🔄 SENÃO atribuir ao responsável: ${responsavelId}`);
            }

            // Atribuir responsável
            if (responsavelId) {
              // Atualiza o fieldValue local para refletir mudança imediata
              fieldValues['responsavelIndividualId'] = responsavelId;
              console.log(`    🎯 Responsável atualizado: ${responsavelId}`);
              
              // Se temos cardId, podemos atualizar no backend
              if (cardId) {
                console.log(`    💾 Atualizando responsável no backend...`);
                fetch(apiUrl(`/cards/${cardId}`), {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ responsavelIndividualId: responsavelId })
                }).then(async (resp) => {
                  const texto = await resp.text();
                  let json: any = null;
                  try { json = JSON.parse(texto); } catch { /* manter texto cru */ }
                  
                  console.log('    📬 Resposta atribuição responsável:', resp.status, json || texto);
                  
                  if (!resp.ok || (json && json.success === false)) {
                    console.warn('    ⚠️ Falha ao atribuir responsável:', json?.error || texto);
                  } else {
                    console.log('    ✅ Responsável atribuído com sucesso');
                  }
                }).catch((err) => {
                  console.error('    ❌ Falha ao atribuir responsável:', err);
                });
              } else {
                console.log(`    ⚠️ CardId não disponível, responsável atualizado apenas localmente`);
              }
            } else {
              console.log(`    ℹ️ Nenhum responsável definido para esta condição`);
            }
          });
        }

        // Criar Card Conectado condicional (SE/ENTÃO/SENÃO) - execução no backend
        if (acao.tipo === 'criar_card_conectado') {
          console.log(`  ⚡ Processando ação ${acaoIndex + 1}: criar_card_conectado`);
          const config = acao.configuracao || {};
          const regrasCardConectado = config.regrasCardConectado || [];

          // Se não há regras condicionais, criar diretamente com a configuração principal
          if (!Array.isArray(regrasCardConectado) || regrasCardConectado.length === 0) {
            const configId = config.configuracaoCardConectadoId;
            if (configId && cardId && changedFieldId === 'initial') {
              console.log(`    🔗 Criando card conectado direto (sem regras) - Config: ${configId}`);
              fetch(apiUrl('/cards-conectados/criar'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  configuracaoId: configId,
                  cardOrigemId: cardId,
                  flowOrigemId: automacao.flowId
                })
              }).then(async (resp) => {
                const data = await resp.json();
                if (data.success) {
                  console.log(`    ✅ Card conectado criado: ${data.card?.id}`);
                } else {
                  console.warn(`    ⚠️ Falha ao criar card conectado: ${data.error}`);
                }
              }).catch(err => console.error('    ❌ Erro ao criar card conectado:', err));
            }
            return;
          }

          regrasCardConectado.forEach((regra: any, regraIndex: number) => {
            const cond = regra?.condicao || {};
            const campoCond = cond.campoId;
            const op = cond.operador;
            const valorCmp = cond.valor;

            if (changedFieldId !== 'initial' && campoCond && campoCond !== changedFieldId) {
              return;
            }

            const valorCampo = fieldValues[campoCond];
            const ok = avaliarCondicao(valorCampo, op, valorCmp);
            console.log(`    🔗 Regra card conectado ${regraIndex + 1} - condição ${ok ? 'ATENDIDA' : 'NÃO ATENDIDA'}`);

            let criarCard = false;
            let configIdParaCriar: string | undefined;

            if (ok) {
              if (regra.acaoEntao?.tipo === 'criar') {
                criarCard = true;
                configIdParaCriar = regra.acaoEntao?.configuracaoCardConectadoId || config.configuracaoCardConectadoId;
              }
            } else if (regra.acaoSenao?.tipo === 'criar') {
              criarCard = true;
              configIdParaCriar = regra.acaoSenao?.configuracaoCardConectadoId || config.configuracaoCardConectadoId;
            }

            if (criarCard && configIdParaCriar && cardId) {
              console.log(`    🔗 Criando card conectado - Config: ${configIdParaCriar}`);
              fetch(apiUrl('/cards-conectados/criar'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  configuracaoId: configIdParaCriar,
                  cardOrigemId: cardId,
                  flowOrigemId: automacao.flowId
                })
              }).then(async (resp) => {
                const data = await resp.json();
                if (data.success) {
                  console.log(`    ✅ Card conectado criado: ${data.card?.id}`);
                } else {
                  console.warn(`    ⚠️ Falha ao criar card conectado: ${data.error}`);
                }
              }).catch(err => console.error('    ❌ Erro ao criar card conectado:', err));
            }
          });
        }

        // Atribuir API condicional (SE/ENTÃO/SENÃO) - chama APIs externas com base no valor de campos
        if (acao.tipo === 'atribuir_api') {
          console.log(`  ⚡ Processando ação ${acaoIndex + 1}: atribuir_api`);
          const config = acao.configuracao || {};
          const regrasApi = config.regrasApi || [];

          if (!Array.isArray(regrasApi) || regrasApi.length === 0) {
            console.log(`  ⚠️ Nenhuma regra de API configurada`);
            return;
          }

          regrasApi.forEach((regra: any, regraIndex: number) => {
            const cond = regra?.condicao || {};
            const campoCond = cond.campoId;
            const op = cond.operador;
            const valorCmp = cond.valor;

            console.log(`  📋 Regra API ${regraIndex + 1} - Campo: ${campoCond}, Op: ${op}, Valor: ${valorCmp}`);

            // Só processar se a mudança afetou o campo da condição
            if (campoCond !== changedFieldId && changedFieldId !== 'initial') {
              console.log(`  ⏭️ Pulando regra API ${regraIndex + 1} - Campo não afetado`);
              return;
            }

            const valorCampo = fieldValues[campoCond];
            const ok = avaliarCondicao(valorCampo, op, valorCmp);
            console.log(`    🌐 Regra API ${regraIndex + 1} - condição ${ok ? 'ATENDIDA' : 'NÃO ATENDIDA'}`);

            // Evitar chamada duplicada: usar state tracking
            const ruleKeyBase = `${automacao.id || automacao.nome || 'auto'}:${cardId || 'noCard'}`;
            const ruleKey = `${ruleKeyBase}:api:${acaoIndex}:${regraIndex}`;
            const prev = lastEmailRuleState.get(ruleKey);

            // Só disparar quando houver transição de estado
            const shouldFire = prev === undefined ? (changedFieldId !== 'initial') : (prev !== ok);
            lastEmailRuleState.set(ruleKey, ok);

            if (!shouldFire) {
              console.log(`    ⏭️ Sem transição de estado para regra API ${regraIndex + 1}`);
              return;
            }

            let acaoApi: any = null;
            let origemAcao = '';

            if (ok && regra.acaoEntao?.tipo === 'chamar_api') {
              acaoApi = regra.acaoEntao;
              origemAcao = 'ENTÃO';
            } else if (!ok && regra.acaoSenao?.tipo === 'chamar_api') {
              acaoApi = regra.acaoSenao;
              origemAcao = 'SENÃO';
            }

            if (!acaoApi || !acaoApi.url) {
              console.log(`    ℹ️ Nenhuma API configurada para ${ok ? 'ENTÃO' : 'SENÃO'}`);
              return;
            }

            // Resolver URL com base no campo de parâmetro
            let urlFinal = acaoApi.url;
            let bodyFinal: any = {};
            const metodo = acaoApi.metodo || 'POST';

            if (acaoApi.campoParametroId) {
              const valorParametro = fieldValues[acaoApi.campoParametroId];
              const textoLimpo = (valorParametro || '').toString().replace(/<[^>]*>/g, '').trim();
              console.log(`    📎 Campo parâmetro: ${acaoApi.campoParametroId}, Valor: "${textoLimpo}"`);

              if (acaoApi.formato === 'numero-empresa') {
                // Formato: NUMERO-EMPRESA (ex: 3536-01)
                const matchOc = textoLimpo.match(/^(\d+)(?:-(\d+))?$/);
                if (matchOc) {
                  const numero = matchOc[1];
                  const empresa = matchOc[2] ? parseInt(matchOc[2], 10) : 1;
                  urlFinal = urlFinal.replace('{valor}', numero);
                  bodyFinal = { empresa };
                  console.log(`    📎 Formato numero-empresa: numero=${numero}, empresa=${empresa}`);
                } else {
                  console.warn(`    ⚠️ Valor "${textoLimpo}" não corresponde ao formato numero-empresa`);
                  return;
                }
              } else {
                // Formato direto: usa o valor limpo
                urlFinal = urlFinal.replace('{valor}', encodeURIComponent(textoLimpo));
              }
            }

            console.log(`    🌐 [${origemAcao}] Chamando API: ${metodo} ${urlFinal}`);
            
            const fetchOptions: any = {
              method: metodo,
              headers: { 'Content-Type': 'application/json' },
            };
            if (metodo !== 'GET' && Object.keys(bodyFinal).length > 0) {
              fetchOptions.body = JSON.stringify(bodyFinal);
            }

            // Determinar label amigável para a API
            const apiLabel = urlFinal.includes('/liberar') ? 'OC Liberada com sucesso' :
                             urlFinal.includes('/estornar') ? 'OC Estornada (voltou para Bloqueada)' :
                             urlFinal.includes('/itens') ? 'Itens da OC consultados' :
                             `API chamada: ${urlFinal}`;

            fetch(urlFinal, fetchOptions).then(async (resp) => {
              const texto = await resp.text();
              let json: any = null;
              try { json = JSON.parse(texto); } catch { /* manter texto cru */ }
              console.log(`    📬 Resposta API: ${resp.status}`, json || texto);
              if (!resp.ok) {
                const msgErro = json?.message || texto || `Erro HTTP ${resp.status}`;
                console.warn(`    ⚠️ Falha na chamada API: ${resp.status}`, msgErro);
                if (onApiResult) onApiResult({ sucesso: false, mensagem: msgErro, url: urlFinal });
              } else {
                console.log(`    ✅ API chamada com sucesso`);
                if (onApiResult) onApiResult({ sucesso: true, mensagem: apiLabel, url: urlFinal });
              }
            }).catch((err) => {
              console.error(`    ❌ Erro ao chamar API:`, err);
              if (onApiResult) onApiResult({ sucesso: false, mensagem: `Erro de conexão: ${err.message || err}`, url: urlFinal });
            });
          });
        }

        // Mover Card Conectado - execução no backend via Python
        if (acao.tipo === 'mover_card_conectado') {
          console.log(`  ⚡ Processando ação ${acaoIndex + 1}: mover_card_conectado`);
          const config = acao.configuracao || {};
          const moverCfg = config.moverCardConectado || {};
          
          if (moverCfg.fluxoDestinoConectadoId && moverCfg.etapaDestinoConectadoId && cardId && changedFieldId === 'initial') {
            console.log(`    🔄 Movendo card conectado - Fluxo: ${moverCfg.fluxoDestinoConectadoId}, Etapa: ${moverCfg.etapaDestinoConectadoId}`);
            fetch(apiUrl('/automacoes/mover-card-conectado'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                cardId: cardId,
                flowId: automacao.flowId,
                moverCardConectado: moverCfg
              })
            }).then(async (resp) => {
              const data = await resp.json();
              if (data.success) {
                console.log(`    ✅ Card conectado movido: ${data.cardConectadoId}`);
              } else {
                console.warn(`    ⚠️ Falha ao mover card conectado: ${data.error}`);
              }
            }).catch(err => console.error('    ❌ Erro ao mover card conectado:', err));
          }
        }
      });
    });
  }
};

export default automationEngine;
